import {
  action,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { TabulatorPortal } from 'API/Generated/TabulatorPortal';

import {
  EventJudgeAocDTO,
  EventJudgeDivisionDTO,
  EventJudgeAgeGroupDTO,
  EventJudgeContestantDTO,
  TabulationEventStatus,
  RegistrationInvoice,
  ContestantRegistrationDetailsDTO,
  EmceeSheetContestantDTO,
} from 'API/Generated/data-contracts';
import { Tabulation } from 'API/Generated/Tabulation';

export type TabulatorAccessLevel = 'Admin' | 'ReadOnly';

export interface PageantTabulatorSession {
  pageantTabulationId: number;
  pageantId: number;
  accessLevel: TabulatorAccessLevel;
  accessCodeLast4?: string;
}

export type TabulatorProgressChangedEvent = {
  eventId: number;

  judgeId: number;

  registeredPageantContestantId: number;

  pageantAvailableEventId: number;

  contestantNumber?: number | string;

  contestantName?: string;

  aocName?: string;

  changeType:
    | 'ScoreSaved'
    | 'ScoreDeleted'
    | 'ScoresDeleted'
    | 'JudgeAocChanged'
    | 'ContestantAocChanged';

  updatedAt: string;
};

export interface TabulatorReadOnlyJudgeAocProgress {
  areaOfCompetition?: EventJudgeAocDTO;

  progressPercent?: number;

  completedRequiredScores?: number;

  totalRequiredScores?: number;

  eventStatus?: number;

eventStatusMessage?: string | null;

resultsPdfUrl?: string | null;

resultsPdfFileName?: string | null;

resultsPdfUpdatedAt?: string | null;

  completedContestants?: number;

  totalContestants?: number;
}

export interface TabulatorReadOnlyJudge {
  eventJudgeId?: number;

  eventId?: number;

  judgeLabel?: string;

  judgeName?: string;

  accessCodeLast4?: string | null;

  status?: string | null;

  isActive?: boolean;

  agreedToTermsAt?: string | null;

  progressPercent?: number;

  completedRequiredScores?: number;

  totalRequiredScores?: number;

   hasTestScore?: boolean;

  areasOfCompetition?: TabulatorReadOnlyJudgeAocProgress[];
}

export interface TabulatorReadOnlyAocProgress {
  areaOfCompetition?: EventJudgeAocDTO;

  progressPercent?: number;

  completedRequiredScores?: number;

  totalRequiredScores?: number;
}

export interface TabulatorReadOnlyDashboard {
  pageantId?: number;

  pageantTabulationId?: number;

  eventName?: string | null;

  eventDate?: string | null;

  totalContestants?: number;

totalJudges?: number;

eventStatus?: number;

eventStatusMessage?: string | null;

resultsPdfUrl?: string | null;

resultsPdfFileName?: string | null;

resultsPdfUpdatedAt?: string | null;

overallProgressPercent?: number;

completedRequiredScores?: number;

totalRequiredScores?: number;

  judges?: TabulatorReadOnlyJudge[];

  areasOfCompetition?: TabulatorReadOnlyAocProgress[];

  divisions?: EventJudgeDivisionDTO[];

  ageGroups?: EventJudgeAgeGroupDTO[];

  contestants?: EventJudgeContestantDTO[];
}

export interface TabulatorJudgeLiveStatus {
  eventId: number;
  judgeId: number;
  liveStatus: 'Online' | 'Offline' | 'Scoring' | 'LoggedIn';
  isOnline: boolean;
  updatedAt: string;
}

export interface TabulatorJudgeLastCompleted {
  judgeId: number;

  registeredPageantContestantId: number;

  pageantAvailableEventId: number;

  contestantNumber?: number | string;

  contestantName?: string;

  aocName?: string;

  completedAt: string;
}

export interface TabulatorLiveFeedItem {
  id: string;

  type:
    | 'judge-complete'
    | 'contestant-aoc-complete'
    | 'age-group-aoc-complete'
    | 'division-aoc-complete'
    | 'test-score'
    | 'side-awards-complete';

  judgeId?: number;

  judgeLabel?: string;

  registeredPageantContestantId: number;

  contestantNumber?: number | string;

  contestantName: string;

  pageantAvailableEventId: number;

  aocName: string;

  ageGroupId?: number;

  ageGroupName?: string;

  divisionId?: number;

  divisionName?: string;

  completedAt: string;
}

const TABULATOR_SESSION_EXPIRES_AT =
  'tabulatorSessionExpiresAt';

const SIX_HOURS_MS =
  6 * 60 * 60 * 1000;

class StaffPortalStore {
  TabulatorPortalService: TabulatorPortal =
    new TabulatorPortal();

    TabulationService: Tabulation =
    new Tabulation();

  CurrentTabulator: PageantTabulatorSession | null =
    null;

  ReadOnlyDashboard: TabulatorReadOnlyDashboard | null =
    null;

  LastProgressChange: TabulatorProgressChangedEvent | null =
    null;

  loading = false;

  error: string | null = null;

  LiveFeedItems: TabulatorLiveFeedItem[] = [];

  
JudgeLiveStatuses: Record<
  number,
  TabulatorJudgeLiveStatus
> = {};

LastCompletedByJudge: Record<
  number,
  TabulatorJudgeLastCompleted
> = {};

setJudgeLiveStatus(
  status: TabulatorJudgeLiveStatus,
): void {
  this.JudgeLiveStatuses = {
    ...this.JudgeLiveStatuses,
    [status.judgeId]: status,
  };
}

setJudgeLastCompleted(
  completed: TabulatorJudgeLastCompleted,
): void {
  this.LastCompletedByJudge = {
    ...this.LastCompletedByJudge,
    [completed.judgeId]: completed,
  };
}

updateSideAwardCompletion(
  divisionId: number,
  ageGroupId?: number,
): void {
  const dashboard =
    this.ReadOnlyDashboard;

  if (!dashboard) {
    return;
  }

  const division =
    dashboard.divisions?.find(
      (item) =>
        Number(
          item.divisionId,
        ) ===
        Number(
          divisionId,
        ),
    );

  if (!division) {
    return;
  }

  // =============================================
  // AGE GROUP
  // =============================================

  if (
    ageGroupId != null
  ) {
    const ageGroup =
      division.ageGroups?.find(
        (item) =>
          Number(
            item.ageGroupId,
          ) ===
          Number(
            ageGroupId,
          ),
      );

    if (!ageGroup) {
      return;
    }

    ageGroup.sideAwardsComplete =
      true;

    if (
      ageGroup
        .sideAwardsTotalCount != null
    ) {
      ageGroup.sideAwardsCompletedCount =
        ageGroup.sideAwardsTotalCount;
    }

    // Reassign the dashboard so MobX
    // definitely notifies observers.
    this.ReadOnlyDashboard = {
      ...dashboard,
    };

    return;
  }

  // =============================================
  // DIVISION WITHOUT AGE GROUPS
  // =============================================

  division.sideAwardsComplete =
    true;

  if (
    division
      .sideAwardsTotalCount != null
  ) {
    division.sideAwardsCompletedCount =
      division.sideAwardsTotalCount;
  }

  this.ReadOnlyDashboard = {
    ...dashboard,
  };
}

  constructor() {
    makeObservable(this, {
      CurrentTabulator: observable,
      ReadOnlyDashboard: observable,
      LastProgressChange: observable,
      loading: observable,
      error: observable,
      JudgeLiveStatuses: observable,
      LastCompletedByJudge: observable,
      LiveFeedItems: observable,


      setCurrentTabulator: action,
      clearCurrentTabulator: action,
      hydrateTabulatorFromSession: action,
      logoutTabulator: action,

      setLastProgressChange: action,
      setJudgeLiveStatus: action,
      setJudgeLastCompleted: action,
        updateSideAwardCompletion: action,

       updateEventStatus: action,
      loginWithAccessCode: action,
      getReadOnlyDashboard: action,
      addLiveFeedItem: action,
clearLiveFeed: action,
    });

    this.hydrateTabulatorFromSession();
  }



addLiveFeedItem(
  item: TabulatorLiveFeedItem,
): void {
  const alreadyExists =
    this.LiveFeedItems.some(
      (existing) =>
        existing.id === item.id,
    );

  if (alreadyExists) {
    return;
  }

  this.LiveFeedItems = [
    item,
    ...this.LiveFeedItems,
  ].slice(0, 100);
}

clearLiveFeed(): void {
  this.LiveFeedItems = [];
}

  async getReadOnlyDashboard(
    pageantId: number,
  ): Promise<TabulatorReadOnlyDashboard | null> {
    this.loading = true;
    this.error = null;

    try {
      const result =
        await this.TabulatorPortalService
          .readonlyDashboardDetail(
            pageantId,
          );

      const dashboard =
        (result?.data as TabulatorReadOnlyDashboard) ??
        null;

      runInAction(() => {
        this.ReadOnlyDashboard =
          dashboard;
      });

      return dashboard;
    } catch (error) {
      console.error(
        'Error loading read only tabulator dashboard:',
        error,
      );

      runInAction(() => {
        this.error =
          'Failed to load tabulator dashboard.';

        this.ReadOnlyDashboard =
          null;
      });

      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

async updateEventStatus(
  pageantId: number,
  eventStatus: TabulationEventStatus,
  eventStatusMessage?: string | null,
  resultsPdfUrl?: string | null,
  resultsPdfFileName?: string | null,
): Promise<boolean> {
  this.loading = true;
  this.error = null;

  try {
    await this.TabulatorPortalService
      .statusUpdate(
        pageantId,
        {
          eventStatus,

          eventStatusMessage:
            eventStatusMessage ?? null,

          resultsPdfUrl:
            resultsPdfUrl ?? null,

          resultsPdfFileName:
            resultsPdfFileName ?? null,
        },
      );

    await this.getReadOnlyDashboard(
      pageantId,
    );

    return true;
  } catch (error) {
    console.error(
      'Error updating tabulator event status:',
      error,
    );

    runInAction(() => {
      this.error =
        'Failed to update event status.';
    });

    return false;
  } finally {
    runInAction(() => {
      this.loading = false;
    });
  }
}

async getContestantRegistrationInvoice(
  pageantId: number,
  contestantId: number,
): Promise<RegistrationInvoice | null> {
  try {
    if (!pageantId || pageantId <= 0) {
      console.error(
        'Invalid pageant ID.',
      );

      return null;
    }

    if (
      !contestantId ||
      contestantId <= 0
    ) {
      console.error(
        'Invalid contestant ID.',
      );

      return null;
    }

    const response =
      await this.TabulationService
        .getRegisteredContestantRegistrationInvoice(
          pageantId,
          contestantId,
        );

    return response.data ?? null;
  } catch (error) {
    console.error(
      `Failed to get registration invoice for contestantId ${contestantId} in pageantId ${pageantId}:`,
      error,
    );

    return null;
  }
}

async getContestantRegistrationDetails(
  pageantId: number,
  contestantId: number,
): Promise<ContestantRegistrationDetailsDTO | null> {
  try {
    if (
      !pageantId ||
      pageantId <= 0
    ) {
      throw new Error(
        `Invalid pageantId: ${pageantId}`,
      );
    }

    if (
      !contestantId ||
      contestantId <= 0
    ) {
      throw new Error(
        `Invalid contestantId: ${contestantId}`,
      );
    }

    console.log(
      '[Contestant Details] Loading:',
      {
        pageantId,
        contestantId,
      },
    );

    const response =
      await this.TabulationService
        .getContestantRegistrationDetails(
          pageantId,
          contestantId,
        );

    console.log(
      '[Contestant Details] Response:',
      response,
    );

    return (
      response?.data ??
      null
    );
  } catch (error) {
    console.error(
      '[Contestant Details] API ERROR:',
      error,
    );

    throw error;
  }
}

async getEmceeSheets(
  pageantId: number,
  divisionId?: number,
  ageGroupId?: number,
): Promise<EmceeSheetContestantDTO[] | null> {
  try {
    if (
      !pageantId ||
      pageantId <= 0
    ) {
      throw new Error(
        `Invalid pageantId: ${pageantId}`,
      );
    }

    const response =
      await this.TabulatorPortalService
        .getEmceeSheets(
          pageantId,
          {
            divisionId,
            ageGroupId,
          },
        );

    return (
      response?.data ??
      null
    );
  } catch (error) {
    console.error(
      '[Emcee Sheets] API ERROR:',
      {
        pageantId,
        divisionId,
        ageGroupId,
        error,
      },
    );

    throw error;
  }
}


  setLastProgressChange(
    change: TabulatorProgressChangedEvent | null,
  ): void {
    this.LastProgressChange =
      change;
  }

  setCurrentTabulator(
    tabulator: PageantTabulatorSession | null,
  ): void {
    this.CurrentTabulator =
      tabulator;

    if (tabulator) {
      sessionStorage.setItem(
        'currentTabulator',
        JSON.stringify(
          tabulator,
        ),
      );

      sessionStorage.setItem(
        TABULATOR_SESSION_EXPIRES_AT,
        String(
          Date.now() +
            SIX_HOURS_MS,
        ),
      );
    } else {
      sessionStorage.removeItem(
        'currentTabulator',
      );

      sessionStorage.removeItem(
        TABULATOR_SESSION_EXPIRES_AT,
      );
    }
  }

  clearCurrentTabulator(): void {
    this.CurrentTabulator =
      null;

    this.ReadOnlyDashboard =
      null;

    this.LastProgressChange =
      null;

      this.LiveFeedItems = [];

      this.JudgeLiveStatuses = {};

this.LastCompletedByJudge = {};

    sessionStorage.removeItem(
      'currentTabulator',
    );

    sessionStorage.removeItem(
      TABULATOR_SESSION_EXPIRES_AT,
    );
  }

  hydrateTabulatorFromSession(): void {
    const expiresAt =
      Number(
        sessionStorage.getItem(
          TABULATOR_SESSION_EXPIRES_AT,
        ),
      );

    if (
      expiresAt &&
      Date.now() > expiresAt
    ) {
      this.logoutTabulator();
      return;
    }

    const raw =
      sessionStorage.getItem(
        'currentTabulator',
      );

    if (!raw) {
      this.CurrentTabulator =
        null;

      return;
    }

    try {
      const tabulator =
        JSON.parse(
          raw,
        ) as PageantTabulatorSession;

      this.CurrentTabulator =
        tabulator;
    } catch (error) {
      console.error(
        'Error hydrating tabulator from session:',
        error,
      );

      this.logoutTabulator();
    }
  }

  logoutTabulator(): void {
    this.CurrentTabulator =
      null;

    this.ReadOnlyDashboard =
      null;

    this.LastProgressChange =
      null;

      this.LiveFeedItems = [];

    this.error =
      null;

      this.JudgeLiveStatuses = {};
      this.LastCompletedByJudge = {};

    sessionStorage.removeItem(
      'currentTabulator',
    );

    sessionStorage.removeItem(
      TABULATOR_SESSION_EXPIRES_AT,
    );

    
  }

  async loginWithAccessCode(
    accessCode: string,
  ): Promise<PageantTabulatorSession | null> {
    this.loading = true;
    this.error = null;

    try {
      const normalizedCode =
        accessCode
          .trim()
          .toUpperCase();

      const result =
        await this.TabulatorPortalService
          .loginCreate({
            accessCode:
              normalizedCode,
          });

      const loginResult =
        result?.data ?? null;

      if (
        !loginResult ||
        !loginResult.pageantId ||
        !loginResult.pageantTabulationId ||
        !loginResult.accessLevel
      ) {
        runInAction(() => {
          this.error =
            'Invalid code or access expired.';

          this.CurrentTabulator =
            null;

          this.ReadOnlyDashboard =
            null;

          this.LastProgressChange =
            null;

          sessionStorage.removeItem(
            'currentTabulator',
          );

          sessionStorage.removeItem(
            TABULATOR_SESSION_EXPIRES_AT,
          );
        });

        return null;
      }

      const tabulatorSession: PageantTabulatorSession =
        {
          pageantId:
            Number(
              loginResult.pageantId,
            ),

          pageantTabulationId:
            Number(
              loginResult.pageantTabulationId,
            ),

          accessLevel:
            loginResult.accessLevel as TabulatorAccessLevel,

          accessCodeLast4:
            loginResult.accessCodeLast4 ??
            undefined,
        };

      runInAction(() => {
        this.setCurrentTabulator(
          tabulatorSession,
        );
      });

      return tabulatorSession;
    } catch (error) {
      console.warn(
        'Tabulator login failed.',
        error,
      );

      runInAction(() => {
        this.error =
          'Invalid code or access expired.';

        this.CurrentTabulator =
          null;

        this.ReadOnlyDashboard =
          null;

        this.LastProgressChange =
          null;

        sessionStorage.removeItem(
          'currentTabulator',
        );

        sessionStorage.removeItem(
          TABULATOR_SESSION_EXPIRES_AT,
        );
      });

      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  get isLoggedIn(): boolean {
    return (
      this.CurrentTabulator !==
      null
    );
  }

  get isAdmin(): boolean {
    return (
      this.CurrentTabulator
        ?.accessLevel ===
      'Admin'
    );
  }

  get isReadOnly(): boolean {
    return (
      this.CurrentTabulator
        ?.accessLevel ===
      'ReadOnly'
    );
  }

  get pageantId(): number | null {
    return (
      this.CurrentTabulator
        ?.pageantId ??
      null
    );
  }

  get pageantTabulationId(): number | null {
    return (
      this.CurrentTabulator
        ?.pageantTabulationId ??
      null
    );
  }
}

let staffPortalStore:
  StaffPortalStore;

export function GetStaffPortalStore(): StaffPortalStore {
  if (!staffPortalStore) {
    staffPortalStore =
      new StaffPortalStore();
  }

  return staffPortalStore;
}